'use client';

import { useMemo, useState } from 'react';
import {
  parseChartRows,
  renderBarChartSvg,
  renderLineChartSvg,
  renderPieChartSvg,
  renderTimelineSvg,
  renderVennSvg,
} from '@/lib/browser-chart-tools';
import { renderSvgToPng } from '@/lib/browser-svg-tools';
import { formatFileSize } from '@/lib/browser-image-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';

export type ChartMakerVariant =
  | 'pie-chart-maker'
  | 'bar-graph-maker'
  | 'line-graph-maker'
  | 'graph-maker'
  | 'venn-diagram-maker'
  | 'timeline-maker';

type ChartKind = 'pie' | 'donut' | 'bar' | 'bar-horizontal' | 'line' | 'venn' | 'timeline';

// Demo rows come from our own published research (see /research), so the
// example is real data with a citable source rather than lorem-ipsum numbers.
const DEMO: Record<string, { data: string; note: string }> = {
  pie: {
    data: 'Photos & video, 58\nApps, 21\nSystem, 12\nMessages, 6\nOther, 3',
    note: 'Example: what typically fills a phone, from the Cleanor storage studies.',
  },
  bar: {
    data: 'TikTok, 936\nFacebook, 622\nInstagram, 549\nSnapchat, 465\nWhatsApp, 285\nSpotify, 202',
    note: 'Example: App Store download sizes in MB, from our app-size-index dataset.',
  },
  line: {
    data: 'Jan, 12\nFeb, 19\nMar, 24\nApr, 22\nMay, 31\nJun, 38\nJul, 45',
    note: 'Example: a monthly series. Paste your own from any spreadsheet.',
  },
  timeline: {
    data: '2023, First prototype\n2024, Public beta\n2025, 1.0 release\n2026, 650 tools live',
    note: 'One "label, event" per line; labels can be years, dates, or steps.',
  },
};

const VARIANT_CONFIG: Record<
  ChartMakerVariant,
  { heading: string; sub: string; kinds: ChartKind[]; defaultKind: ChartKind }
> = {
  'pie-chart-maker': {
    heading: 'Make a pie chart',
    sub: 'Paste "label, value" rows, get a clean pie or donut, and export SVG or PNG. Everything renders locally.',
    kinds: ['pie', 'donut'],
    defaultKind: 'pie',
  },
  'bar-graph-maker': {
    heading: 'Make a bar graph',
    sub: 'Paste "label, value" rows, get a labeled bar graph, and export SVG or PNG. Everything renders locally.',
    kinds: ['bar', 'bar-horizontal'],
    defaultKind: 'bar',
  },
  'line-graph-maker': {
    heading: 'Make a line graph',
    sub: 'Paste "label, value" rows in order, get a clean line graph, and export SVG or PNG. Everything renders locally.',
    kinds: ['line'],
    defaultKind: 'line',
  },
  'graph-maker': {
    heading: 'Make a graph',
    sub: 'Paste your data once and switch between bar, line, and pie until the story reads. Export SVG or PNG, all local.',
    kinds: ['bar', 'bar-horizontal', 'line', 'pie', 'donut'],
    defaultKind: 'bar',
  },
  'venn-diagram-maker': {
    heading: 'Make a Venn diagram',
    sub: 'Two sets and their overlap, sized by the numbers you enter, exported as SVG or PNG. Everything renders locally.',
    kinds: ['venn'],
    defaultKind: 'venn',
  },
  'timeline-maker': {
    heading: 'Make a timeline',
    sub: 'One "label, event" per line becomes a clean horizontal timeline you can export as SVG or PNG. All local.',
    kinds: ['timeline'],
    defaultKind: 'timeline',
  },
};

const KIND_LABELS: Record<ChartKind, string> = {
  pie: 'Pie',
  donut: 'Donut',
  bar: 'Bars',
  'bar-horizontal': 'Bars, horizontal',
  line: 'Line',
  venn: 'Venn',
  timeline: 'Timeline',
};

function demoFor(kind: ChartKind) {
  if (kind === 'timeline') return DEMO.timeline;
  if (kind === 'line') return DEMO.line;
  if (kind === 'pie' || kind === 'donut') return DEMO.pie;
  return DEMO.bar;
}

export default function BrowserChartMaker({ variant }: { variant: ChartMakerVariant }) {
  const config = VARIANT_CONFIG[variant];

  const [kind, setKind] = useState<ChartKind>(config.defaultKind);
  const [title, setTitle] = useState('');
  const [dataText, setDataText] = useState(demoFor(config.defaultKind).data);
  const [vennA, setVennA] = useState({ label: 'Set A', size: 40 });
  const [vennB, setVennB] = useState({ label: 'Set B', size: 30 });
  const [vennOverlap, setVennOverlap] = useState(12);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);

  const parsed = useMemo(() => parseChartRows(dataText), [dataText]);

  const svg = useMemo(() => {
    if (kind === 'venn') {
      return renderVennSvg(
        {
          aLabel: vennA.label,
          aSize: vennA.size,
          bLabel: vennB.label,
          bSize: vennB.size,
          overlap: vennOverlap,
        },
        { title },
      );
    }
    if (kind === 'timeline') {
      const events = dataText
        .split(/\r?\n/)
        .map((line) => {
          const match = line.trim().match(/^(.*?)[,;\t]\s*(.+)$/);
          return match ? { label: match[1].trim(), title: match[2].trim() } : null;
        })
        .filter((event): event is { label: string; title: string } => Boolean(event));
      return renderTimelineSvg(events, { title });
    }
    if (kind === 'pie' || kind === 'donut') {
      return renderPieChartSvg(parsed.rows, { title, donut: kind === 'donut' });
    }
    if (kind === 'line') {
      return renderLineChartSvg(parsed.rows, { title });
    }
    return renderBarChartSvg(parsed.rows, { title, horizontal: kind === 'bar-horizontal' });
  }, [kind, parsed.rows, dataText, title, vennA, vennB, vennOverlap]);

  function downloadBlob(blob: Blob, name: string) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  async function runExport(format: 'svg' | 'png') {
    if (!svg) {
      setErrorMessage('Add at least two valid data rows first.');
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another chart right now.',
      );

      if (format === 'svg') {
        const blob = new Blob([svg], { type: 'image/svg+xml' });
        setResultSize(blob.size);
        downloadBlob(blob, `${variant}.svg`);
      } else {
        const rendered = await renderSvgToPng(svg, {
          scale: 2,
          backgroundMode: 'white',
          customBackground: '#ffffff',
          format: 'png',
        });
        setResultSize(rendered.size);
        downloadBlob(rendered, `${variant}.png`);
      }

      setCreditMessage(formatToolCreditSuccess('Chart', credits));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'The chart could not be exported.');
    } finally {
      setIsSaving(false);
    }
  }

  const showRowEditor = kind !== 'venn';
  const demo = demoFor(kind);

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{config.heading}</h2>
          <p>{config.sub}</p>
        </div>
        <span className="tool-live-badge">Chart</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          {svg ? (
            <div
              aria-label="Chart preview"
              className="tool-image-preview-canvas"
              style={{ background: '#fff', borderRadius: 12, overflow: 'hidden' }}
              dangerouslySetInnerHTML={{ __html: svg }}
            />
          ) : (
            <p className="tool-live-panel-note">
              Add at least two valid rows and the preview appears here.
            </p>
          )}
        </div>

        <aside className="tool-live-panel">
          {config.kinds.length > 1 ? (
            <label>
              Chart type
              <select
                onChange={(event) => {
                  const next = event.target.value as ChartKind;
                  const prevDemo = demoFor(kind).data;
                  setKind(next);
                  // Swap in the matching example only if the user has not typed
                  // their own data yet; never overwrite real input.
                  if (dataText === prevDemo) {
                    setDataText(demoFor(next).data);
                  }
                }}
                value={kind}
              >
                {config.kinds.map((option) => (
                  <option key={option} value={option}>
                    {KIND_LABELS[option]}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <label>
            Chart title (optional)
            <input onChange={(event) => setTitle(event.target.value)} type="text" value={title} />
          </label>

          {showRowEditor ? (
            <label>
              Data, one row per line
              <textarea
                onChange={(event) => setDataText(event.target.value)}
                rows={8}
                spellCheck={false}
                value={dataText}
              />
              <small>{demo.note}</small>
            </label>
          ) : (
            <>
              <label>
                First set: name and size
                <span style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    onChange={(event) => setVennA((v) => ({ ...v, label: event.target.value }))}
                    style={{ flex: 2 }}
                    type="text"
                    value={vennA.label}
                  />
                  <input
                    min={1}
                    onChange={(event) =>
                      setVennA((v) => ({ ...v, size: Number(event.target.value) }))
                    }
                    style={{ flex: 1 }}
                    type="number"
                    value={vennA.size}
                  />
                </span>
              </label>
              <label>
                Second set: name and size
                <span style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    onChange={(event) => setVennB((v) => ({ ...v, label: event.target.value }))}
                    style={{ flex: 2 }}
                    type="text"
                    value={vennB.label}
                  />
                  <input
                    min={1}
                    onChange={(event) =>
                      setVennB((v) => ({ ...v, size: Number(event.target.value) }))
                    }
                    style={{ flex: 1 }}
                    type="number"
                    value={vennB.size}
                  />
                </span>
              </label>
              <label>
                Overlap size
                <input
                  min={0}
                  onChange={(event) => setVennOverlap(Number(event.target.value))}
                  type="number"
                  value={vennOverlap}
                />
                <small>How many items are in both sets. The lens is sized from this number.</small>
              </label>
            </>
          )}

          {parsed.errors.length && showRowEditor && kind !== 'timeline' ? (
            <p className="support-feedback support-feedback-error">{parsed.errors[0]}</p>
          ) : null}

          {resultSize ? (
            <div className="tool-live-stats">
              <div>
                <strong>{formatFileSize(resultSize)}</strong>
                <span>exported locally, no watermark</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      <div className="hero-actions tool-live-actions">
        <button
          className="button"
          disabled={!svg || isSaving}
          onClick={() => void runExport('png')}
          type="button"
        >
          {isSaving ? 'Exporting…' : 'Download PNG'}
        </button>
        <button
          className="button button-ghost"
          disabled={!svg || isSaving}
          onClick={() => void runExport('svg')}
          type="button"
        >
          Download SVG
        </button>
      </div>

      {errorMessage ? (
        <p className="support-feedback support-feedback-error">{errorMessage}</p>
      ) : null}
      {creditMessage ? <p className="support-feedback">{creditMessage}</p> : null}
    </article>
  );
}
